import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  ArrowLeft,
  CreditCard,
  Download,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Landmark,
} from 'lucide-react';
import { toast } from 'sonner';
import StuckInvoicesPanel from './StuckInvoicesPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const fmtUsd = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'succeeded' || s === 'paid') {
    return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Paid</Badge>;
  }
  if (s === 'refunded') {
    return <Badge className="bg-red-500/20 text-red-300 border-red-500/30">Refunded</Badge>;
  }
  if (s === 'initiated' || s === 'pending') {
    return <Badge className="bg-white/10 text-slate-300 border-white/20">Pending</Badge>;
  }
  return <Badge variant="outline" className="border-white/20 text-slate-300 capitalize">{status || 'Unknown'}</Badge>;
};

const methodBadge = (p) => {
  const m = p.payment_method;
  if (m === 'us_bank_account' || m === 'ach') {
    return (
      <Badge className="bg-sky-500/20 text-sky-300 border-sky-500/30 gap-1">
        <Landmark className="h-3 w-3" /> ACH
      </Badge>
    );
  }
  if (m === 'card') {
    return (
      <Badge className="bg-white/10 text-slate-200 border-white/20 gap-1">
        <CreditCard className="h-3 w-3" /> Card
      </Badge>
    );
  }
  return <span className="text-muted-foreground text-xs">—</span>;
};

const feeRuleLabel = (p) => {
  const r = p.fee_rule;
  if (!r || !r.mode) return p.platform_fee_usd > 0 ? '' : 'no fee rule at checkout';
  if (r.mode === 'off') return 'fee waived';
  if (r.mode === 'percent') {
    const cap = r.cap_cents ? ` · $${(r.cap_cents / 100).toFixed(0)} cap` : '';
    return `${r.percent}%${cap}`;
  }
  if (r.mode === 'flat') return `$${((r.flat_cents || 0) / 100).toFixed(2)} flat`;
  return '';
};

/**
 * Platform-wide Stripe Connect payments ledger (Owner Admin only).
 *
 * Filters: workspace_id (via ?workspace_id=), status, date range.
 * Displays paginated payments with gross/fee totals and CSV export.
 */
export default function OwnerAdminPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceIdFilter = searchParams.get('workspace_id') || '';

  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [method, setMethod] = useState(searchParams.get('method') || 'all');
  const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('to') || '');
  const [skip, setSkip] = useState(0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Reset pagination when filters change
  useEffect(() => {
    setSkip(0);
  }, [workspaceIdFilter, status, method, dateFrom, dateTo]);

  const buildQuery = useCallback(
    (extraSkip = skip) => {
      const params = new URLSearchParams();
      if (workspaceIdFilter) params.set('workspace_id', workspaceIdFilter);
      if (status && status !== 'all') params.set('status', status);
      if (method && method !== 'all') params.set('method', method);
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      params.set('skip', String(extraSkip));
      params.set('limit', String(PAGE_SIZE));
      return params.toString();
    },
    [workspaceIdFilter, status, method, dateFrom, dateTo, skip],
  );

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/payment-stats/platform/payments?${buildQuery()}`,
        { credentials: 'include' },
      );
      if (!r.ok) {
        toast.error('Failed to load payments ledger');
        setData(null);
      } else {
        setData(await r.json());
      }
    } catch (e) {
      toast.error('Failed to load payments ledger');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const clearWorkspaceFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('workspace_id');
    setSearchParams(next, { replace: true });
  };

  const handleExportCsv = async () => {
    if (!data || !data.payments?.length) {
      toast.info('Nothing to export in current filter.');
      return;
    }
    setExporting(true);
    try {
      // Pull the full filtered set (up to 500) for export
      const params = new URLSearchParams();
      if (workspaceIdFilter) params.set('workspace_id', workspaceIdFilter);
      if (status && status !== 'all') params.set('status', status);
      if (method && method !== 'all') params.set('method', method);
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      params.set('skip', '0');
      params.set('limit', '500');

      const r = await fetch(
        `${API_URL}/api/payment-stats/platform/payments?${params.toString()}`,
        { credentials: 'include' },
      );
      if (!r.ok) {
        toast.error('CSV export failed');
        return;
      }
      const j = await r.json();
      const rows = (j.payments || []).map((p) => ({
        ...p,
        payment_method: p.payment_method === 'us_bank_account' ? 'ach' : p.payment_method || 'unknown',
        fee_rule: feeRuleLabel(p),
      }));

      const headers = [
        'created_at',
        'workspace_name',
        'workspace_id',
        'invoice_no',
        'customer_name',
        'customer_email',
        'amount_usd',
        'surcharge_usd',
        'refund_amount_usd',
        'net_amount_usd',
        'platform_fee_usd',
        'fee_rule',
        'payment_method',
        'status',
        'stripe_payment_intent_id',
        'stripe_charge_id',
      ];
      const csv = [
        headers.join(','),
        ...rows.map((p) =>
          headers
            .map((h) => {
              const v = p[h] ?? '';
              const s = String(v).replace(/"/g, '""');
              return /[",\n]/.test(s) ? `"${s}"` : s;
            })
            .join(','),
        ),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `countbucks-payments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} payment(s)`);
    } catch (e) {
      toast.error('CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const pagination = data?.pagination || { total: 0, skip: 0, limit: PAGE_SIZE };
  const totals = data?.totals || { gross_usd: 0, platform_fee_usd: 0 };
  const payments = data?.payments || [];
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / PAGE_SIZE));

  const workspaceHeader = useMemo(() => {
    if (!workspaceIdFilter) return null;
    // First row will carry workspace_name
    const first = payments[0];
    return first?.workspace_name || workspaceIdFilter;
  }, [workspaceIdFilter, payments]);

  return (
    <div className="space-y-6" data-testid="owner-admin-payments-ledger">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="text-muted-foreground"
            >
              <Link to="/owner-admin">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div className="p-2.5 rounded-lg bg-emerald-500/10">
              <CreditCard className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Platform Payments Ledger</h1>
              <p className="text-sm text-muted-foreground">
                Every Stripe Connect invoice payment CountBucks has facilitated
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={handleExportCsv}
          disabled={exporting || loading || !payments.length}
          variant="outline"
          className="border-white/20"
          data-testid="payments-export-csv"
        >
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {/* Stuck checkouts (paid on Stripe, invoice unpaid) + manual re-check */}
      <StuckInvoicesPanel />

      {/* Filter chip when scoped to a workspace */}
      {workspaceIdFilter && (
        <div className="flex items-center gap-2 text-sm">
          <Badge
            variant="outline"
            className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 py-1 pl-2 pr-1 gap-1"
            data-testid="payments-workspace-filter-chip"
          >
            <span>Workspace: {workspaceHeader || workspaceIdFilter}</span>
            <button
              onClick={clearWorkspaceFilter}
              className="ml-1 rounded hover:bg-emerald-500/20 p-0.5"
              data-testid="payments-clear-workspace-filter"
              aria-label="Clear workspace filter"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
          <Link
            to={`/owner-admin/customers/${workspaceIdFilter}`}
            className="text-xs text-muted-foreground hover:text-white inline-flex items-center gap-1"
          >
            Open customer profile <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Payments in filter
            </p>
            <p className="text-3xl font-bold text-white mt-1" data-testid="totals-count">
              {pagination.total || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Gross volume
            </p>
            <p className="text-3xl font-bold text-white mt-1" data-testid="totals-gross">
              {fmtUsd(totals.gross_usd)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Platform fees collected
            </p>
            <p className="text-3xl font-bold text-emerald-400 mt-1" data-testid="totals-fee">
              {fmtUsd(totals.platform_fee_usd)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Effective fee rate
            </p>
            <p className="text-3xl font-bold text-white mt-1" data-testid="totals-fee-rate">
              {totals.gross_usd > 0
                ? `${((totals.platform_fee_usd / totals.gross_usd) * 100).toFixed(2)}%`
                : '—'}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">
              Platform default: 0.6% · $25 cap
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-white/15">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wide">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger
                className="bg-white/5 border-white/15 text-white mt-1"
                data-testid="payments-filter-status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid (non-refunded)</SelectItem>
                <SelectItem value="succeeded">Succeeded</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wide">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger
                className="bg-white/5 border-white/15 text-white mt-1"
                data-testid="payments-filter-method"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="ach">ACH (bank)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wide">From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-white/5 border-white/15 text-white mt-1"
              data-testid="payments-filter-from"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wide">To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-white/5 border-white/15 text-white mt-1"
              data-testid="payments-filter-to"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="border-white/20 w-full"
              onClick={() => {
                setStatus('all');
                setMethod('all');
                setDateFrom('');
                setDateTo('');
              }}
              data-testid="payments-filter-reset"
            >
              Clear filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-card border-white/15">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-white text-base">
              {workspaceIdFilter ? 'Workspace payments' : 'All payments'}
            </CardTitle>
            <CardDescription className="text-muted-foreground/70">
              Newest first · {pagination.total || 0} match
              {(pagination.total || 0) === 1 ? '' : 'es'}
            </CardDescription>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={skip <= 0 || loading}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                data-testid="payments-prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={skip + PAGE_SIZE >= pagination.total || loading}
                onClick={() => setSkip(skip + PAGE_SIZE)}
                data-testid="payments-next-page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full bg-white/5" />
              ))}
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No payments found for this filter.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Workspace</TableHead>
                  <TableHead className="text-muted-foreground">Invoice</TableHead>
                  <TableHead className="text-muted-foreground">Customer</TableHead>
                  <TableHead className="text-muted-foreground">Method</TableHead>
                  <TableHead className="text-muted-foreground text-right">Gross</TableHead>
                  <TableHead className="text-muted-foreground text-right">Fee</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow
                    key={p.payment_id || p.stripe_payment_intent_id}
                    className="border-white/10 hover:bg-white/[0.03]"
                    data-testid={`payment-row-${p.payment_id || p.stripe_payment_intent_id}`}
                  >
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                      {fmtDate(p.created_at)}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/owner-admin/customers/${p.workspace_id}`}
                        className="text-white hover:text-emerald-400 truncate max-w-[200px] inline-block align-middle"
                        title={p.workspace_name}
                      >
                        {p.workspace_name || p.workspace_id}
                      </Link>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {p.invoice_no || '—'}
                    </TableCell>
                    <TableCell className="text-slate-300 truncate max-w-[200px]">
                      {p.customer_name || p.customer_email || '—'}
                    </TableCell>
                    <TableCell data-testid={`payment-method-${p.payment_id || p.stripe_payment_intent_id}`}>
                      {methodBadge(p)}
                    </TableCell>
                    <TableCell className="text-right text-white font-medium">
                      {fmtUsd(p.net_amount_usd)}
                      {p.surcharge_usd > 0 && (
                        <div className="text-[10px] text-amber-400/80">
                          incl. {fmtUsd(p.surcharge_usd)} surcharge
                        </div>
                      )}
                      {p.refund_amount_usd > 0 && (
                        <div className="text-[10px] text-red-400">
                          −{fmtUsd(p.refund_amount_usd)} refunded
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-emerald-400 font-medium">
                      {fmtUsd(p.platform_fee_usd)}
                      <div className="text-[10px] text-muted-foreground font-normal whitespace-nowrap">
                        {feeRuleLabel(p)}
                      </div>
                    </TableCell>
                    <TableCell>{statusBadge(p.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
